<template>
  <div>
    <div ref="mapContainer" class="map"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import L from "leaflet";
import apiClient from "/api.js";
import "leaflet/dist/leaflet.css";
import {useI18n} from 'vue-i18n'

const props = defineProps({
  isOrganizer: Boolean, // Oprávnění z rodičovské komponenty
});

const { t } = useI18n()
const API_KEY = process.env.VUE_APP_API_KEY_MAPS;
const route = useRoute();
const eventId = ref(null);
const mapContainer = ref(null);
let map = null;
const markers = ref([]);

const DEFAULT_CENTER = [49.7414, 15.3380]; // Výchozí souřadnice
const DEFAULT_ZOOM = 7; // Výchozí zoom

// Přizpůsobená ikona markeru
const markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.2/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// rychlý test na "lat,lon" v desetinném tvaru
const isLatLngQuick = (q) => {
  return /^[-+]?\d+(\.\d+)?\s*[,;]\s*[-+]?\d+(\.\d+)?$/.test(q.trim());
};

const fitOrCenter = (entity) => {
  if (entity?.bbox?.length === 4) {
    const [minLon, minLat, maxLon, maxLat] = entity.bbox;
    map.fitBounds([[minLat, minLon],[maxLat, maxLon]], { padding: [20,20] });
  } else if (entity?.position) {
    map.setView([entity.position.lat, entity.position.lon], 14);
  }
};

// geocode function
const geocode = async (query) => {
  const base = "https://api.mapy.com/v1/geocode";
  const call = async (params) => {
    const url = `${base}?${new URLSearchParams(params).toString()}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Geocode failed");
    const data = await res.json();
    // API vrací pole entit; bereme první
    const items = data?.items || data?.results || data || [];
    return Array.isArray(items) ? items[0] : null;
  };

  // 1) zkus souřadnice (pokrývá různé formáty přes type=coordinate)
  const coordParams = {
    query,
    type: "coordinate",
    lang: "cs",
    limit: "1",
    apikey: API_KEY,
  };
  let ent = null;
  try {
    ent = await call(coordParams);
  } catch (_) {}

  // 2) když nic, zkus běžné geokódování názvu místa
  if (!ent) {
    const textParams = {
      query,
      // type neudávám -> default regional + poi
      lang: "cs",
      limit: "1",
      apikey: API_KEY,
    };
    ent = await call(textParams);
  }

  if (!ent) throw new Error("Nenalezeno");
  return ent;
};

onMounted(async () => {
  if (route.params.id) {
    eventId.value = parseInt(route.params.id);
  } else {
    console.error("Chyba: eventId není v URL!");
    return;
  }

  if (!mapContainer.value) return;

  // Inicializace Leaflet mapy
  map = L.map(mapContainer.value).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
   
  // Původní turistická bez možnosti přepínat mezi vrstvama
  // L.tileLayer("https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=ve7dHe0hCMOIfqLdj4UbT-dC06RqAUKeEs9-lk6Q1bA", {attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',}).addTo(map);

  // **2. Definice mapových vrstev**
  const tileLayers = {
    [t('map.map_layer_basic')]: L.tileLayer(`https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${API_KEY}`, {
      minZoom: 0,
      maxZoom: 19,
      // attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }),
    [t('map.map_layer_tourist')]: L.tileLayer(`https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=${API_KEY}`, {
      minZoom: 0,
      maxZoom: 19,
      // attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }),
    [t('map.map_layer_winter')]: L.tileLayer(`https://api.mapy.cz/v1/maptiles/winter/256/{z}/{x}/{y}?apikey=${API_KEY}`, {
      minZoom: 0,
      maxZoom: 19,
      // attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }),
    [t('map.map_layer_aerial')]: L.tileLayer(`https://api.mapy.cz/v1/maptiles/aerial/256/{z}/{x}/{y}?apikey=${API_KEY}`, {
      minZoom: 0,
      maxZoom: 19,
      // attribution: '<a href="https://api.mapy.cz/copyright" target="_blank">&copy; Seznam.cz a.s. a další</a>',
    }),
  };

  // **Zabráníme nechtěné interakci mimo mapu, aby se windows item nepřepínal na etapu**
  L.DomEvent.disableClickPropagation(mapContainer.value);
  L.DomEvent.disableScrollPropagation(mapContainer.value);

  // **3. Nastavení výchozí vrstvy (Outdoor - Turistická)**
  tileLayers[t('map.map_layer_tourist')].addTo(map);

  // **4. Přidání ovládání pro přepínání vrstev**
  L.control.layers(tileLayers).addTo(map);

  // search control (UI) 
  const SearchControl = L.Control.extend({
    options: { position: "bottomleft" },
    onAdd() {
      const c = L.DomUtil.create("div", "leaflet-bar search-box");
      c.innerHTML = `
        <input type="text" placeholder="${t("map.search_placeholder") ?? "Hledat místo nebo souřadnice"}" class="map-search-input"/>
        <button class="map-search-btn">🔎</button>
      `;
      const input = c.querySelector(".map-search-input");
      const btn = c.querySelector(".map-search-btn");

      const run = async () => {
        const q = (input.value || "").trim();
        if (!q) return;
        try {
          // pro rychlý "lat,lon" v deset. tvaru rovnou centrovat
          if (isLatLngQuick(q)) {
            const [lat, lon] = q.split(/[;,]/).map((s) => parseFloat(s));
            // malá „obálka“ pro efekt fitBounds (bez markeru)
            const d = 0.002; // ~200–300 m dle zeměpisné šířky
            map.fitBounds([[lat - d, lon - d], [lat + d, lon + d]], { padding: [20,20] });
            return;
          }
          const ent = await geocode(q);
          fitOrCenter(ent);
        } catch (e) {
          console.error(e);
          alert(t("map.search_not_found") ?? "Nenašlo se nic.");
        }
      };

      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        run();
      });
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          run();
        }
      });

      // zamez scrollu mapy při práci v inputu
      L.DomEvent.disableClickPropagation(c);
      L.DomEvent.disableScrollPropagation(c);
      return c;
    },
  });

  if (props.isOrganizer) {
    new SearchControl().addTo(map);
  }


  await loadMarkers();

  if (props.isOrganizer) {
    map.on("click", async (e) => {
      const description = prompt(t('map.prompt_add_marker'));
      if (!description) return;
      await addMarker(e.latlng.lat, e.latlng.lng, description);
    });
  }
});

// Načtení markerů s popupem u jednoho. Nemůže se zobrazit vše najednou, to tento popup neumožňuje
const loadMarkers = async () => {
  try {
    if (!eventId.value) {
      console.error("Chyba: eventId není definován!");
      return;
    }

    const response = await apiClient.get(`event/map_marker/?event_id=${eventId.value}`);
    markers.value = [];

    if (response.data.length > 0) {
      const firstMarker = response.data[0];
      map.setView([firstMarker.lat, firstMarker.lng], 12);
    } else {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }

    const markerGroup = L.layerGroup(); // Skupina pro všechny popupy

    response.data.forEach(({ id, lat, lng, description }) => {
      let popupContent = `
  <b>
    <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" 
      target="_blank"
      style="color: #0078D7;"
      onmouseover="this.style.textDecoration='underline';"
      onmouseout="this.style.textDecoration='none';"
    >
     ${description} 
    </a>
  </b>
<a
  href="https://mapy.cz/zakladni?source=coor&id=${lng},${lat}&ds=1"
  target="_blank" rel="noopener" title="mapy.com" class="ml-1">
  <i class="mdi mdi-map" style="color:#1eae00;"></i>

</a>
`;
/*{ <i class="mdi mdi-map-marker-path" style="font-size: 18px;"></i> }*/


      if (props.isOrganizer) {
        popupContent += `
          <button class="delete-btn" data-id="${id}" 
            style="background:transparent; border:none; cursor:pointer; font-size: 18px; color: red; margin-left: 10px; ">
            <i class="mdi mdi-delete"></i>
          </button>
        `;
      }
      const marker = L.marker([lat, lng], { icon: markerIcon }).addTo(map);
      const popup = L.popup({ autoClose: false, closeOnClick: false, closeButton: false, maxWidth: 150, className: "custom-popup" }) // Zabrání zavření popupu po kliknutí
        .setLatLng([lat, lng])
        .setContent(popupContent);

      marker.bindPopup(popup);
      markerGroup.addLayer(popup); // Přidáme popup do skupiny

      marker._icon.dataset.id = id;

      // Přidání posluchače události "popupopen"
      marker.on("popupopen", (e) => {
      const contentNode = e.popup._contentNode;
      contentNode.addEventListener("touchstart", (event) => {
        event.stopPropagation();
      });
      contentNode.addEventListener("touchmove", (event) => {
        event.stopPropagation();
      });
      if (props.isOrganizer) {
        const deleteBtn = contentNode.querySelector(".delete-btn");
        if (deleteBtn && !deleteBtn.dataset.bound) {
          deleteBtn.dataset.bound = "1";
          deleteBtn.addEventListener("click", (event) => {
            event.stopPropagation();
            confirmDeleteMarker(id, description);
          });
        }
      }
    });

      markers.value.push({ id, lat, lng, marker });
      });

      map.addLayer(markerGroup); // Přidáme celou skupinu popupů do mapy

      // přidání loga mapy.cz

    // const LogoControl = L.Control.extend({
    // options: {
    //   position: 'bottomleft',
    // },

  //   onAdd: function () {
  //     const container = L.DomUtil.create('div');
  //     const link = L.DomUtil.create('a', '', container);

  //     link.setAttribute('href', 'http://mapy.cz/');
  //     link.setAttribute('target', '_blank');
  //     link.innerHTML = '<img src="https://api.mapy.cz/img/api/logo.svg" />';
  //     L.DomEvent.disableClickPropagation(link);

  //     return container;
  //   },
  // });

  // // finally we add our LogoControl to the map
  // new LogoControl().addTo(map);

    } catch (error) {
      console.error("Chyba při načítání markerů:", error);
    }
  };


// potvrzení smazání (stejná filozofie jako prompt u create)
const confirmDeleteMarker = async (markerId, description) => {
  const ok = window.confirm(
    t('map.confirm_delete_marker', { description }) 
      ?? `Opravdu smazat bod "${description}"?`
  );
  if (!ok) return;

  await deleteMarker(markerId);
};


// Přidání markeru do API a zobrazení na mapě
const addMarker = async (lat, lng, description) => {
  try {
    if (!eventId.value) {
      console.error("Chyba: eventId není definován!");
      return;
    }

    await apiClient.post(`event/map_marker/?event_id=${eventId.value}`, {
      lat,
      lng,
      description,
    });

    await loadMarkers();

  } catch (error) {
    console.error("Chyba:", error);
  }
};

// Smazání markeru z API a mapy
const deleteMarker = async (markerId) => {
  try {
    if (!markerId) {
      console.error("Chyba: markerId není definován!");
      return;
    }

    await apiClient.delete(`event/map_marker/?marker_id=${markerId}`);

    markers.value = markers.value.filter((m) => m.id !== markerId);

    map.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer._icon.dataset.id == markerId) {
        map.removeLayer(layer);
      }
    });

    console.log("Marker smazán:", markerId);

    // Pokud nejsou žádné markery po smazání, vrátíme výchozí pohled
    // if (markers.value.length === 0) {
    //   map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    // }
  } catch (error) {
    console.error("Chyba při mazání markeru:", error);
  }
};

// Sledování změn v route a aktualizace eventId
watch(
  () => route.params.id,
  (newId) => {
    eventId.value = parseInt(newId);
    loadMarkers();
  }
);
</script>

<style scoped>
.map {
  width: 100%;
  height: 330px;
  border:1px solid grey
}

.delete-btn {
  color: white;
  border: none;
  padding: 5px;
  cursor: pointer;
}

::v-deep(.leaflet-control-attribution) {
  display: none !important;
}


::v-deep(.search-box){
  background:#fff !important;
  opacity:1 !important;
  padding:8px;
  border:1px solid #ccc;
  border-radius:6px;
  box-shadow:0 1px 4px rgba(0,0,0,.2);
  display:flex; gap:4px; align-items:center;
}
::v-deep(.map-search-input){
  width:220px; border:1px solid #ccc; padding:2px 4px; border-radius:4px;
  background:#fff;
}
::v-deep(.map-search-btn){
  background:#fff; padding:2px 4px; border-radius:4px; cursor:pointer;
}


</style>
